import type { Step as RcStep } from '@sb/remote-control/types';
import type { Space, StepKind } from '@sb/routine-runner';

import {
  type ValidationRule,
  type RoutineView,
  type ValidationContext,
} from '../../types';
import { ActionRequiredError, type WarningFinding } from '../../util/errors';
import { validateCodeExpression } from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class AddOffsetRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.AddOffset', 'AddOffset');
  }

  protected validate({
    children,
    args,
    routine,
    ctx,
    warn,
  }: CanonicalValidateArgs): void {
    type AddOffsetArgs = Extract<
      RcStep.Arguments,
      { argumentKind: 'AddOffset' }
    >;
    const a = args as AddOffsetArgs;
    warn(this.checkExpressions(a));
    this.checkRequiresMoveArm(children);
    this.checkFrameValid(a, routine, ctx);
  }

  /**
   * Validate translation expressions. Invalid syntax throws; deprecated
   * syntax is recorded via `warn`.
   *
   * @throws {ActionRequiredError}
   */
  private checkExpressions(
    args: Extract<RcStep.Arguments, { argumentKind: 'AddOffset' }>,
  ): Array<WarningFinding | undefined> {
    return [
      validateCodeExpression(args.translationX as any, 'translationX', 'X'),
      validateCodeExpression(args.translationY as any, 'translationY', 'Y'),
      validateCodeExpression(args.translationZ as any, 'translationZ', 'Z'),
    ];
  }

  /**
   * Ensure an Add Offset container includes at least one MoveArm child.
   * Throws ActionRequiredError if not found.
   *
   * @throws {ActionRequiredError}
   */
  private checkRequiresMoveArm(
    children: Array<{ stepKind: StepKind | string }>,
  ): void {
    const ok = children.some(
      (nestedStep) =>
        nestedStep.stepKind === 'MoveArmTo' ||
        nestedStep.stepKind === 'MoveArmToV2',
    );

    if (!ok) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Add Offset requires a Move Arm step',
      });
    }
  }

  /**
   * Validate selected frame exists (for custom frames).
   * Throws ActionRequiredError if invalid.
   *
   * @throws {ActionRequiredError}
   */
  private checkFrameValid(
    args: Extract<RcStep.Arguments, { argumentKind: 'AddOffset' }>,
    routine: RoutineView | { space?: Space.Item[] },
    ctx: ValidationContext,
  ): void {
    const allSpace = this.getSpaceItems(routine, ctx);

    const frame = args.frame as string | null;
    const isCustom = frame !== 'base' && frame !== 'world';

    const exists = allSpace.some(
      (item) => item.id === frame && item.kind === 'plane',
    );

    if (isCustom && !exists) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Selected frame is not valid',
      });
    }
  }
}

export const AddOffsetValidation: ValidationRule =
  new AddOffsetRule().toValidationRule();
